export const ROUTES = {
	GLOBAL: "/global",
	GLOBAL_CALIBRATION: "/global/calibration",
	GLOBAL_PRIORS: "/global/priors",
	GLOBAL_UNKNOWN_LABELS: "/global/unknown-labels",
	LOGIN: "/login",
	NOT_FOUND: "*",
	ORGANIZATION: "/organization",
	ORGANIZATION_COSTS: "/organization/costs",
	ORGANIZATION_INSTALLATIONS: "/organization/installations",
	ORGANIZATION_MAP: "/organization/map",
	ORGANIZATION_PLAUSIBILITY_SETTINGS: "/organization/plausibility",
	ORGANIZATION_PRIORS: "/organization/priors",
	ORGANIZATION_REANALYSIS: "/organization/reanalysis",
	ORGANIZATION_REANALYSIS_EVENT: "/organization/reanalysis/events/:eventId",
	ORGANIZATION_REPLAY: "/organization/replay",
	ORGANIZATION_SCENE_PROPOSAL: "/organization/scene-proposals/:proposalId",
	ORGANIZATION_SCENE_PROPOSALS: "/organization/scene-proposals",
	ORGANIZATION_VERIFICATION: "/organization/verifications/:verificationId",
	ORGANIZATION_VERIFICATIONS: "/organization/verifications",
	ROOT: "/",
} as const;

export interface NavItem {
	href: string;
	label: string;
	/**
	 * Shown only to users whose access record carries `globalAccess`. On an
	 * organization-scoped item this marks an internal-only view of one
	 * customer's data (the verification, scene-proposal and reanalysis
	 * screens); the API enforces the same flag on the routes behind it.
	 */
	requiresGlobalAccess?: boolean;
}

/**
 * The sidebar, in display order. Two groups: what is scoped to the
 * organization picked in the header, and what is shared across every
 * organization. Every Shadow Realm user has the first group, scoped to the
 * organizations on their access record; the second group and the internal
 * items in the first appear only with `globalAccess`.
 */
export const NAV = {
	global: [
		{
			href: ROUTES.GLOBAL_UNKNOWN_LABELS,
			label: "Unknown labels",
			requiresGlobalAccess: true,
		},
		{
			href: ROUTES.GLOBAL_PRIORS,
			label: "Global priors",
			requiresGlobalAccess: true,
		},
		{
			href: ROUTES.GLOBAL_CALIBRATION,
			label: "Calibration",
			requiresGlobalAccess: true,
		},
	],
	organization: [
		{ href: ROUTES.ORGANIZATION_MAP, label: "Map Configuration" },
		{ href: ROUTES.ORGANIZATION_INSTALLATIONS, label: "Lookout Installations" },
		{
			href: ROUTES.ORGANIZATION_PLAUSIBILITY_SETTINGS,
			label: "Plausibility settings",
		},
		{ href: ROUTES.ORGANIZATION_PRIORS, label: "Scoped priors" },
		{
			href: ROUTES.ORGANIZATION_VERIFICATIONS,
			label: "Verifications",
			requiresGlobalAccess: true,
		},
		{
			href: ROUTES.ORGANIZATION_SCENE_PROPOSALS,
			label: "Scene proposals",
			requiresGlobalAccess: true,
		},
		{
			href: ROUTES.ORGANIZATION_REANALYSIS,
			label: "Reanalysis",
			requiresGlobalAccess: true,
		},
		{
			href: ROUTES.ORGANIZATION_REPLAY,
			label: "Replay",
			requiresGlobalAccess: true,
		},
		{
			href: ROUTES.ORGANIZATION_COSTS,
			label: "Costs",
			requiresGlobalAccess: true,
		},
	],
} as const satisfies Record<string, readonly NavItem[]>;

export const plausibilityVerificationPath = (verificationId: string) =>
	`${ROUTES.ORGANIZATION_VERIFICATIONS}/${verificationId}`;

export const reanalysisEventPath = (eventId: string) =>
	`${ROUTES.ORGANIZATION_REANALYSIS}/events/${eventId}`;

export const sceneProposalPath = (proposalId: string) =>
	`${ROUTES.ORGANIZATION_SCENE_PROPOSALS}/${proposalId}`;
